import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface ITourStep {
    elementId: string;
    title: string;
    content: string;
    position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
    nextLabel?: string; // Texto personalizado para el botón (ej: 'Continuar')
    hideNext?: boolean; // Si es true, oculta el botón de siguiente (útil para esperas automáticas)
    action?: () => void; // Acción opcional al avanzar a este paso (o completar el anterior)
    prevAction?: () => void; // Acción opcional al volver atrás desde este paso
}

@Injectable({
    providedIn: 'root'
})
export class AppTourService {
    private _active = new BehaviorSubject<boolean>(false);
    private _currentStep = new BehaviorSubject<number>(0);
    private _steps = new BehaviorSubject<ITourStep[]>([]);
    private _pendingTour: string | null = null;
    private _returnUrl: string | null = null;
    private _onStop: (() => void) | null = null;
    private _helpRequested = new Subject<void>();
    private _isHelpAvailable = new BehaviorSubject<boolean>(false);
    private _stepOffset = 0;
    private _totalStepsOverride: number | null = null;

    active$: Observable<boolean> = this._active.asObservable();
    currentStep$: Observable<number> = this._currentStep.asObservable();
    steps$: Observable<ITourStep[]> = this._steps.asObservable();
    helpRequested$: Observable<void> = this._helpRequested.asObservable();
    isHelpAvailable$: Observable<boolean> = this._isHelpAvailable.asObservable();

    constructor() {}

    /**
     * Finaliza el tour
     */
    stop(): void {
        if (this._onStop) {
            this._onStop();
            this._onStop = null;
        }
        this._active.next(false);
        this._steps.next([]);
        this._currentStep.next(0);
    }

    /**
     * Desplaza la vista hacia el elemento del paso actual
     * @param elementId ID del elemento (ej: '#tour-campo')
     */
    private _scrollToElement(elementId: string): void {
        if (!elementId) return;
        
        // Pequeño delay para asegurar que el DOM esté listo o el diálogo abierto
        setTimeout(() => {
            const element = document.querySelector(elementId);
            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });
            }
        }, 100);
    }

    /**
     * Inicia un tour con una configuración de pasos específica
     * @param steps Lista de pasos del tour
     * @param onStop Acción opcional al finalizar o cerrar el tour
     * @param totalSteps Total de pasos global (opcional para numeración continua)
     * @param stepOffset Desplazamiento inicial (opcional para numeración continua)
     */
    start(steps: ITourStep[], onStop?: () => void, totalSteps?: number, stepOffset?: number): void {
        this._steps.next(steps);
        this._currentStep.next(0);
        this._active.next(true);
        this._onStop = onStop || null;
        this._totalStepsOverride = totalSteps || null;
        this._stepOffset = stepOffset || 0;
        
        if (steps.length > 0) {
            this._scrollToElement(steps[0].elementId);
        }
    }

    /**
     * Avanza al siguiente paso
     */
    next(): void {
        const currentIndex = this._currentStep.value;
        const nextStep = currentIndex + 1;
        const steps = this._steps.value;

        // Avanzar el índice PRIMERO
        if (nextStep < steps.length) {
            this._currentStep.next(nextStep);
            this._scrollToElement(steps[nextStep].elementId);
        } else {
            this.stop();
            return;
        }

        // Luego ejecutar la acción del paso que acabamos de dejar
        if (steps[currentIndex]?.action) {
            steps[currentIndex].action();
        }
    }

    /**
     * Retorna el índice del paso actual
     */
    getCurrentStep(): number {
        return this._currentStep.value;
    }

    /**
     * Retorna si el tour está activo
     */
    isActive(): boolean {
        return this._active.value;
    }

    /**
     * Vuelve al paso anterior
     */
    previous(): void {
        const currentIndex = this._currentStep.value;
        const prevStep = currentIndex - 1;
        const steps = this._steps.value;

        if (prevStep >= 0) {
            // Ejecutar la acción de retroceso del paso que estamos dejando
            if (steps[currentIndex]?.prevAction) {
                steps[currentIndex].prevAction();
            }
            this._currentStep.next(prevStep);
            this._scrollToElement(steps[prevStep].elementId);
        }
    }

    /**
     * Guarda un tour para ser iniciado en la próxima pantalla
     */
    setPendingTour(tourName: string | null): void {
        if (tourName) {
            localStorage.setItem('pending_tour', tourName);
        } else {
            localStorage.removeItem('pending_tour');
        }
    }

    /**
     * Recupera y limpia el tour pendiente
     */
    consumePendingTour(): string | null {
        const tour = localStorage.getItem('pending_tour');
        localStorage.removeItem('pending_tour');
        return tour;
    }

    /**
     * Guarda la URL de origen para volver al finalizar
     */
    setReturnUrl(url: string | null): void {
        this._returnUrl = url;
    }

    /**
     * Recupera la URL de origen
     */
    getReturnUrl(): string | null {
        return this._returnUrl;
    }

    /**
     * Dispara una solicitud de ayuda global
     */
    requestHelp(): void {
        this._helpRequested.next();
    }
    /**
     * Define si hay ayuda disponible en la pantalla actual
     */
    setHelpAvailable(available: boolean): void {
        this._isHelpAvailable.next(available);
    }

    /**
     * Retorna el progreso del tour (actual/total) considerando el offset
     */
    getStepProgress(): { current: number, total: number } {
        const current = this._currentStep.value + 1 + this._stepOffset;
        const total = this._totalStepsOverride || this._steps.value.length;
        return { current, total };
    }
}
